import React, { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, MapPin, Send, CheckCircle2 } from "lucide-react";
import useReveal from "../hooks/useReveal";
import WindPalm from "../components/WindPalm";
import AnimatedText from "../components/AnimatedText";
import SEO from "../components/SEO";
import { SITE, PALMS } from "../mock";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    arrival: "",
    departure: "",
    guests: "2",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 20000,
      });
      const data = res.data || {};
      // Always store locally as a backup
      try {
        const queue = JSON.parse(localStorage.getItem("casa_elio_leads") || "[]");
        queue.push({ ...form, ts: Date.now(), to: SITE.email, server_id: data.id });
        localStorage.setItem("casa_elio_leads", JSON.stringify(queue));
      } catch (_) {}

      setSubmitted(true);
      if (data.email_sent) {
        toast.success("Thank you — your inquiry is on its way to Casa Elio.");
      } else {
        toast.success("Thank you — we have your inquiry.");
      }
    } catch (err) {
      toast.error("Couldn't send right now. Please WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Contact — Plan your stay at Casa Elio"
        description="Send us a note about your trip and we'll come back with availability, rates, and a few thoughts on the slower side of the coast. Or reach us directly on WhatsApp at +505 7769 8277."
        path="/contact"
      />
      <section className="relative pt-40 pb-12 md:pt-48 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
          Contact
        </div>
        <h1 className="reveal font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-[#1f2a24] max-w-5xl">
          <span className="block"><AnimatedText text="Plan your stay" stagger={32} baseDelay={150} /></span>
          <span className="block opacity-80" style={{ fontStyle: "italic", fontWeight: 500 }}>
            <AnimatedText text="at Casa Elio." stagger={30} baseDelay={550} />
          </span>
        </h1>
        <p className="reveal mt-8 max-w-2xl text-[#3a4a40] text-base md:text-lg leading-relaxed">
          Tell us a little about your trip and we’ll come back with rates,
          availability, and a few thoughts on the slower side of the coast.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 grid md:grid-cols-12 gap-12 md:gap-16">
        {/* Form */}
        <div className="md:col-span-7">
          {submitted ? (
            <div className="reveal bg-[#ebe3d2] p-10 md:p-14 flex flex-col items-start">
              <CheckCircle2 className="text-[#2f4438] mb-5" size={28} />
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#1f2a24]">
                Message received.
              </h3>
              <p className="mt-4 text-[#3a4a40] leading-relaxed">
                Thank you, {form.name?.split(" ")[0] || "friend"}. We’ll be in
                touch by email at{" "}
                <span className="text-[#1f2a24]">{form.email}</span>. For an
                immediate reply, message us on WhatsApp.
              </p>
              <a
                href={`https://wa.me/${SITE.whatsappRaw}`}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-6 py-3 bg-[#25D366] text-white hover:bg-[#1ebe5a] transition-colors"
              >
                <MessageCircle size={14} /> Continue on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="reveal space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Name" name="name" value={form.name} onChange={handle} required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={handle} required />
              </div>
              <div className="grid sm:grid-cols-3 gap-6">
                <Field label="Arrival" name="arrival" type="date" value={form.arrival} onChange={handle} />
                <Field label="Departure" name="departure" type="date" value={form.departure} onChange={handle} />
                <Field label="Guests" name="guests" type="number" min="1" max="6" value={form.guests} onChange={handle} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handle}
                  placeholder="Tell us about your trip, group, and what you’re hoping for."
                  className="w-full bg-transparent border-b border-[#1f2a24]/25 focus:border-[#2f4438] outline-none py-3 text-[#1f2a24] placeholder:text-[#1f2a24]/40 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-7 py-4 bg-[#2f4438] text-[#f5f0e6] hover:bg-[#1f2a24] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send Inquiry"}
                <Send size={14} />
              </button>
              <p className="text-[11px] text-[#3a4a40]/70">
                Inquiries are forwarded to {SITE.email}.
              </p>
            </form>
          )}
        </div>

        {/* Side info */}
        <aside className="md:col-span-4 md:col-start-9">
          <div className="reveal space-y-8 text-[#1f2a24]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-2">
                Location
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-[#2f4438]" />
                <p className="leading-relaxed">San Juan del Sur,<br />Rivas, Nicaragua</p>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-2">
                WhatsApp
              </div>
              <a
                href={`https://wa.me/${SITE.whatsappRaw}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 hover:text-[#b6724a] transition-colors"
              >
                <MessageCircle size={16} className="mt-1 text-[#2f4438]" />
                <span>{SITE.whatsapp}</span>
              </a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-2">
                Email
              </div>
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-start gap-3 hover:text-[#b6724a] transition-colors"
              >
                <Mail size={16} className="mt-1 text-[#2f4438]" />
                <span>{SITE.email}</span>
              </a>
            </div>
            <div className="pt-6 border-t border-[#1f2a24]/15">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-3">
                Or book directly
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm link-underline"
                >
                  Booking.com →
                </a>
                <a
                  href={SITE.airbnbUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm link-underline"
                >
                  Airbnb →
                </a>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, min, max }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#3a4a40] mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      min={min}
      max={max}
      className="w-full bg-transparent border-b border-[#1f2a24]/25 focus:border-[#2f4438] outline-none py-3 text-[#1f2a24] placeholder:text-[#1f2a24]/40 transition-colors"
    />
  </div>
);

export default Contact;
